import { Link, useLocation } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { type OrgProjectScope, parseOrgProjectScope } from "./home-path";

/**
 * Builds a typed router `Link` to the clean home for `scope` — the project
 * dashboard, the org index, or the app root — mirroring {@link buildHomePath}.
 *
 * Returns a bare `Link` element intended for a Base UI `render` prop, which
 * merges the button's own children (icon + label) into the anchor. Using a typed
 * `to` + `params` keeps route/param validation intact without a string cast.
 */
export function homeLink(scope: OrgProjectScope | null): ReactElement {
  if (scope?.projectId) {
    return (
      <Link
        params={{
          organizationId: scope.organizationId,
          projectId: scope.projectId,
        }}
        to="/orgs/$organizationId/projects/$projectId/home"
      />
    );
  }

  if (scope) {
    return (
      <Link
        params={{ organizationId: scope.organizationId }}
        to="/orgs/$organizationId"
      />
    );
  }

  return <Link to="/" />;
}

/**
 * The ready-to-render home `Link` for the current router location's scope, for a
 * Button's `render` prop — sends the user back to the org/project they were in
 * rather than the account default. Use this from a function component inside the
 * router; call {@link homeLink} directly when the scope comes from elsewhere
 * (e.g. `/403` search params). Screens rendered outside the router (the top-level
 * class error boundary) resolve a plain href via {@link deriveHomePath} instead,
 * since a router `Link` needs router context.
 */
export function useHomeLink(): ReactElement {
  const { pathname } = useLocation();
  return homeLink(parseOrgProjectScope(pathname));
}
